import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Phone,
  BarChart3,
  Settings,
  CreditCard,
  Users,
  Monitor,
  Menu,
  X,
  Bell,
  User,
  LogOut,
  Mic,
  Building2,
  GitBranch,
  Activity,
  BookOpen,
  Contact,
  Shield,
  TrendingUp,
  Brain,
  Filter,
  FileAudio,
  TestTube2,
  Megaphone,
  Plug,
  Headphones,
  PhoneForwarded,
  ChevronDown,
  ChevronRight,
  ShieldCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import clsx from 'clsx';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { connectionStatus, isConnected } = useWebSocket();
  const location = useLocation();

  // Check if user is system administrator
  const isAdmin = user?.email === 'brian@muleworks.co';

  // Collapsible sidebar groups state
  const [expandedGroups, setExpandedGroups] = useState({
    'Sales & CRM': true,
    'Communication': true,
    'AI & Knowledge': true,
    'People': true,
    'Account': true,
    'Developer': true
  });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Build grouped navigation
  const navigationGroups = [
    {
      items: [
        { name: 'Overview', href: '/app', icon: LayoutDashboard }
      ]
    },
    {
      label: 'Sales & CRM',
      items: [
        { name: 'Sales Pipeline', href: '/app/sales-pipeline', icon: Filter },
        { name: 'Sales Campaigns', href: '/app/sales-campaigns', icon: TrendingUp },
        { name: 'Contacts', href: '/app/contacts', icon: Contact },
        { name: 'Business Profile', href: '/app/business-profile', icon: Building2 }
      ]
    },
    {
      label: 'Communication',
      items: [
        { name: 'Calls', href: '/app/calls', icon: Phone },
        { name: 'Call Screening', href: '/app/call-screening', icon: ShieldCheck },
        { name: 'Employee Comms', href: '/app/employee-communications', icon: Megaphone },
        { name: 'Conversation Flows', href: '/app/conversation-flows', icon: GitBranch },
        ...(isAdmin ? [
          { name: 'Dialer', href: '/app/dialer', icon: PhoneForwarded },
          { name: 'Live Calls', href: '/app/live-calls', icon: Headphones }
        ] : [])
      ]
    },
    {
      label: 'AI & Knowledge',
      items: [
        { name: 'Technical Expert', href: '/app/technical-expert', icon: Brain },
        { name: 'Knowledge Base', href: '/app/knowledge', icon: BookOpen },
        ...(isAdmin ? [
          { name: 'IVR Audio Assets', href: '/app/ivr-audio-assets', icon: FileAudio },
          { name: 'Demo Area', href: '/app/demo-area', icon: Mic }
        ] : [])
      ]
    },
    {
      label: 'People',
      items: [
        { name: 'Staff Directory', href: '/app/staff', icon: Users },
        { name: 'Users', href: '/app/users', icon: Users }
      ]
    },
    {
      label: 'Account',
      items: [
        { name: 'Analytics', href: '/app/analytics', icon: BarChart3 },
        { name: 'Integrations', href: '/app/integrations', icon: Plug },
        { name: 'Billing', href: '/app/billing', icon: CreditCard },
        { name: 'Settings', href: '/app/settings', icon: Settings }
      ]
    },
    ...(isAdmin ? [{
      label: 'Developer',
      items: [
        { name: 'QA Testing', href: '/app/qa', icon: TestTube2 },
        { name: 'Monitoring', href: '/app/monitoring', icon: Activity },
        { name: 'Admin Dashboard', href: '/app/admin', icon: Shield }
      ]
    }] : [])
  ];

  // Flat list for header title lookup
  const navigation = navigationGroups.flatMap(g => g.items);

  const isActiveLink = (href) => {
    if (href === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 flex z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={clsx(
        'fixed inset-y-0 left-0 flex flex-col w-64 bg-white shadow-lg transform z-50 md:translate-x-0 md:static md:inset-0 transition-transform duration-300 ease-in-out',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Alfred Voice</span>
          </div>
          <button
            className="md:hidden p-1 rounded-md hover:bg-gray-100"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navigationGroups.map((group, groupIndex) => {
            if (!group.label) {
              // Ungrouped items (e.g., Overview)
              return group.items.map((item) => {
                const Icon = item.icon;
                const active = isActiveLink(item.href);
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={clsx(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                      active
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={clsx('w-5 h-5 mr-3', active ? 'text-blue-700' : 'text-gray-400')} />
                    {item.name}
                  </NavLink>
                );
              });
            }

            const isExpanded = expandedGroups[group.label];
            const hasActiveChild = group.items.some(item => isActiveLink(item.href));

            return (
              <div key={group.label} className={groupIndex > 0 ? 'pt-2' : ''}>
                <button
                  onClick={() => toggleGroup(group.label)}
                  className={clsx(
                    'flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors duration-200',
                    hasActiveChild && !isExpanded
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <span>{group.label}</span>
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5" />
                    : <ChevronRight className="w-3.5 h-3.5" />
                  }
                </button>
                {isExpanded && (
                  <div className="mt-1 space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActiveLink(item.href);
                      return (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={clsx(
                            'flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ml-1',
                            active
                              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          )}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <Icon className={clsx('w-4 h-4 mr-3', active ? 'text-blue-700' : 'text-gray-400')} />
                          {item.name}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-2 mb-3">
            <div className={clsx(
              'w-2 h-2 rounded-full',
              isConnected ? 'bg-green-500' : 'bg-red-500'
            )} />
            <span className="text-xs text-gray-500">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div className="text-xs text-gray-500">
            <div className="font-medium">{user?.tenant?.companyName}</div>
            <div className="capitalize">{user?.tenant?.plan} Plan</div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex justify-between items-center px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                className="md:hidden p-2 rounded-md hover:bg-gray-100"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>

              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {navigation.find(item => isActiveLink(item.href))?.name || 'Dashboard'}
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Connection status indicator */}
              <div className="flex items-center space-x-2">
                <div className={clsx(
                  'w-2 h-2 rounded-full',
                  isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                )} />
                <span className="text-sm text-gray-600">
                  {isConnected ? 'Live' : 'Offline'}
                </span>
              </div>

              {/* Notifications */}
              <button className="relative p-2 rounded-md hover:bg-gray-100">
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* User menu */}
              <div className="relative group">
                <button className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {user?.firstName} {user?.lastName}
                  </span>
                </button>

                {/* Dropdown menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user?.email}</div>
                    </div>

                    <NavLink
                      to="/dashboard/settings"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </NavLink>

                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;